import { ArrowLeft, ArrowRight } from '@mui/icons-material';
import { cloneDeep } from 'lodash-es';
import {
	SovosButton,
	SovosCircularProgress,
	SovosDialog,
	SovosDialogActions,
	SovosDialogContent,
	SovosDialogTitle,
	SovosDivider,
	SovosMenuItem,
	SovosSelect,
	SovosTypography,
} from 'mosaic-react';
import React, { useState } from 'react';

import FormStepLayout from 'src/components/FormStepLayout';
import type { LocationMappingField } from 'src/interfaces/LocationMapping';
import type {
	FormMappedField,
	LocationMappingEditorFormState,
	LocationMappingEditorFormStep,
} from 'src/pages/locationMapping/locationMappingEditor/LocationMappingEditorTypes';

import styles from './StepMap.module.scss';

const StepMap: LocationMappingEditorFormStep<{
	isSubmitting: boolean;
	selectedMap: LocationMappingEditorFormState['mappedFields'];
	setSelectedMap: (
		files: LocationMappingEditorFormState['mappedFields']
	) => void;
	mappingFields?: LocationMappingField[];
	isNewLocationMapping: boolean;
}> = ({
	isSubmitting,
	onContinue,
	selectedMap,
	setSelectedMap,
	mappingFields = [],
	isNewLocationMapping,
}) => {
	const [mappedHeaderInUseToChange, setMappedHeaderInUseToChange] =
		useState<FormMappedField | null>(null);

	const mappedHeaders = selectedMap?.reduce(
		(disabledHeader, selectedHeader) => {
			if (selectedHeader.targetField) {
				disabledHeader[selectedHeader.sourceField] = true;
			}

			return disabledHeader;
		},
		{} as Record<string, boolean>
	);

	const missingRequiredFields = mappingFields
		.filter((field) => field.required)
		.reduce<Record<string, boolean>>((missingRequiredHeaders, mappingField) => {
			if (
				!selectedMap?.find(
					(selectedField) =>
						selectedField.targetField === mappingField.fieldName
				)
			) {
				missingRequiredHeaders[mappingField.fieldName] = true;
			}

			return missingRequiredHeaders;
		}, {});

	const [showErrors, setShowErrors] = useState(false);

	const errors = Object.keys(missingRequiredFields).map(
		(missingField) => `Please select ${missingField} header`
	);

	const handleContinue = () => {
		if (errors.length === 0) {
			onContinue();
		} else if (!showErrors) {
			setShowErrors(true);
		}
	};

	const handleResetMapping = () => {
		setSelectedMap(
			selectedMap?.map((element) => ({
				...element,
				targetField: null,
			}))
		);
	};

	const handleChangeSelect = ({
		sourceField,
		targetField,
	}: FormMappedField) => {
		if (selectedMap) {
			const newSelectedMap = cloneDeep(selectedMap);

			const currentMap = newSelectedMap.find(
				(selectedMap) => selectedMap.targetField === targetField
			);

			if (currentMap) {
				currentMap.targetField = null;
			}

			const changedMap = newSelectedMap.find(
				(selectedMap) => selectedMap.sourceField === sourceField
			);

			if (changedMap) {
				changedMap.targetField = targetField;
			}

			setSelectedMap(newSelectedMap);
			setMappedHeaderInUseToChange(null);
		}
	};

	const getSovosHeaders = () => {
		return (
			<div className={styles.headers}>
				<SovosDivider />
				<div className={styles['headers-extension']} />
				{mappingFields?.map((header) => (
					<div key={header.fieldName} className={styles.header}>
						<div className={styles['header-content']}>
							<div className={styles['header-content-box']}>
								<p className={styles['header-content-box-text']}>
									{header.fieldName}
								</p>
							</div>
						</div>
						<ArrowRight className={styles['arrow-right']} />
					</div>
				))}
			</div>
		);
	};

	const getCustomHeaders = () => {
		return (
			<div className={styles['custom-headers']}>
				<SovosDivider />
				<div className={styles['custom-headers-extension']} />
				<div className={styles['custom-header']}>
					<div className={styles['custom-header-extension']}>
						{mappingFields.map((header) => {
							return (
								<div
									key={header.fieldName}
									className={styles['custom-header-content']}
								>
									<ArrowLeft className={styles['arrow-left']} />
									<div className={styles['custom-header-content-box']}>
										<div className={styles['custom-header-content-box-select']}>
											<SovosSelect
												error={
													showErrors && missingRequiredFields[header.fieldName]
												}
												label={
													header.required
														? '*Required - Select your header'
														: '(Optional) - Select your header'
												}
												onChange={(
													event: React.ChangeEvent<HTMLInputElement>
												) => {
													const mappedField: FormMappedField = {
														sourceField: event.target.value,
														targetField: header.fieldName,
													};

													if (mappedHeaders?.[mappedField.sourceField]) {
														setMappedHeaderInUseToChange(mappedField);
													} else {
														handleChangeSelect(mappedField);
													}
												}}
												value={
													selectedMap?.find(
														(selectedMap) =>
															selectedMap.targetField === header.fieldName
													)?.sourceField || ''
												}
												variant='outlined'
												sx={{
													'&&& label': {
														color:
															showErrors &&
															missingRequiredFields[header.fieldName]
																? 'error.main'
																: undefined,
													},
												}}
											>
												<SovosMenuItem value=''></SovosMenuItem>
												{selectedMap?.map((field) => {
													return (
														<SovosMenuItem
															key={field.sourceField}
															value={field.sourceField}
														>
															{field.sourceField}
														</SovosMenuItem>
													);
												})}
											</SovosSelect>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</div>
		);
	};

	return (
		<FormStepLayout
			title='Map'
			subTitle='Map the headers in your uploaded file to the Sovos headers.'
			errors={errors}
			showErrors={showErrors}
			onContinue={handleContinue}
			continueButton={
				<SovosButton disabled={isSubmitting} onClick={handleContinue}>
					{isSubmitting ? (
						<SovosCircularProgress size={20} />
					) : isNewLocationMapping ? (
						'Create'
					) : (
						'Save'
					)}
				</SovosButton>
			}
		>
			<div className={styles.map}>
				<div className={styles.header}>
					<div className={styles['header-container']}>
						<div className={styles['sovos-headers']}>
							<h5 className={styles['sovos-headers-extension']}>
								Sovos headers
							</h5>
						</div>
						<div className={styles['file-headers']}>
							<h5 className={styles['file-headers-extension']}>Your headers</h5>
						</div>
						<div className={styles['clear-mapping']}>
							<SovosButton
								className={styles['clear-mapping-extension']}
								variant='text'
								id='resetLocationMapping'
								onClick={handleResetMapping}
							>
								Clear mapping
							</SovosButton>
						</div>
					</div>
				</div>
				<div className={styles.content}>
					{getSovosHeaders()}
					<hr />
					{getCustomHeaders()}
				</div>
			</div>

			<SovosDialog width='small' open={Boolean(mappedHeaderInUseToChange)}>
				<SovosDialogTitle>Header already in use</SovosDialogTitle>
				<SovosDialogContent>
					<SovosTypography sx={{ mb: 3 }}>
						Header fields can only be used once per mapping.
					</SovosTypography>
					<SovosTypography>
						The header you selected is already mapped to another Sovos header.
						Would you like to map it here instead?
					</SovosTypography>
				</SovosDialogContent>
				<SovosDialogActions>
					<SovosButton
						onClick={() => {
							setMappedHeaderInUseToChange(null);
						}}
						variant='text'
					>
						Cancel
					</SovosButton>
					<SovosButton
						onClick={() => {
							if (mappedHeaderInUseToChange)
								handleChangeSelect(mappedHeaderInUseToChange);
						}}
					>
						Yes, continue with mapping
					</SovosButton>
				</SovosDialogActions>
			</SovosDialog>
		</FormStepLayout>
	);
};

export default StepMap;
